import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["description", "prefixCls", "message", "banner", "className", "style", "onMouseEnter", "onMouseLeave", "onClick", "afterClose", "showIcon", "closable", "closeText", "closeIcon", "action"];
import CheckCircleFilled from "@ant-design/icons/CheckCircleFilled";
import CloseCircleFilled from "@ant-design/icons/CloseCircleFilled";
import ExclamationCircleFilled from "@ant-design/icons/ExclamationCircleFilled";
import InfoCircleFilled from "@ant-design/icons/InfoCircleFilled";
import InfoCircleOutlined from "@ant-design/icons/InfoCircleOutlined";
import classNames from "classnames";
import CSSMotion from "rc-motion";
import * as React from "react";
import { IconClose16pxRegular, IconError20pxRegular, IconSuccess20pxRegular, IconTips20pxRegular } from "@pionex-web-kit/icons";
import getDataOrAriaProps from "../hack/_util/getDataOrAriaProps";
import { replaceElement } from "../hack/_util/reactNode";
import { ConfigContext } from "./../ConfigProvider";
import ErrorBoundary from "./ErrorBoundary";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var iconMapFilled = {
  success: CheckCircleFilled,
  info: InfoCircleFilled,
  error: CloseCircleFilled,
  warning: ExclamationCircleFilled
};
var iconMapOutlined = {
  success: IconSuccess20pxRegular,
  info: InfoCircleOutlined,
  error: IconError20pxRegular,
  warning: IconTips20pxRegular
};
var Alert = function Alert(_ref) {
  var _classNames2;
  var description = _ref.description,
    customizePrefixCls = _ref.prefixCls,
    message = _ref.message,
    banner = _ref.banner,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? "" : _ref$className,
    style = _ref.style,
    onMouseEnter = _ref.onMouseEnter,
    onMouseLeave = _ref.onMouseLeave,
    onClick = _ref.onClick,
    afterClose = _ref.afterClose,
    _ref$showIcon = _ref.showIcon,
    showIcon = _ref$showIcon === void 0 ? true : _ref$showIcon,
    closable = _ref.closable,
    closeText = _ref.closeText,
    _ref$closeIcon = _ref.closeIcon,
    closeIcon = _ref$closeIcon === void 0 ? /*#__PURE__*/_jsx(IconClose16pxRegular, {
      size: 12
    }) : _ref$closeIcon,
    action = _ref.action,
    props = _objectWithoutProperties(_ref, _excluded);
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    closed = _React$useState2[0],
    setClosed = _React$useState2[1];
  var close = React.useCallback(function () {
    setClosed(true);
  }, []);
  var ref = React.useRef();
  var _React$useContext = React.useContext(ConfigContext),
    getPrefixCls = _React$useContext.getPrefixCls,
    direction = _React$useContext.direction;
  var prefixCls = getPrefixCls("alert", customizePrefixCls);
  var handleClose = function handleClose(e) {
    var _props$onClose;
    setClosed(true);
    (_props$onClose = props.onClose) === null || _props$onClose === void 0 ? void 0 : _props$onClose.call(props, e);
  };
  var getType = function getType() {
    var type = props.type;
    if (type !== undefined) {
      return type;
    }
    // banner 模式默认为警告
    return banner ? "warning" : "info";
  };

  // closeable when closeText is assigned
  var isClosable = closeText ? true : closable;
  var type = getType();
  var renderIconNode = function renderIconNode() {
    var icon = props.icon;
    // use outline icon in alert with description
    // const iconType = (description ? iconMapOutlined : iconMapFilled)[type] || null;
    var iconType = iconMapOutlined[type] || null;
    if (icon) {
      return replaceElement(icon, /*#__PURE__*/_jsx("span", {
        className: "".concat(prefixCls, "-icon"),
        children: icon
      }), function () {
        return {
          className: classNames("".concat(prefixCls, "-icon"), _defineProperty({}, icon.props.className, icon.props.className))
        };
      });
    }
    return /*#__PURE__*/React.createElement(iconType, {
      className: "".concat(prefixCls, "-icon"),
      size: 20
    });
  };
  var renderCloseIcon = function renderCloseIcon() {
    return isClosable ? /*#__PURE__*/_jsx("button", {
      type: "button",
      onClick: handleClose,
      className: "".concat(prefixCls, "-close-icon"),
      tabIndex: 0,
      children: closeText ? /*#__PURE__*/_jsx("span", {
        className: "".concat(prefixCls, "-close-text"),
        children: closeText
      }) : closeIcon
    }) : null;
  };

  // banner 模式默认有 Icon
  var isShowIcon = banner && showIcon === undefined ? true : showIcon;
  var alertCls = classNames(prefixCls, "".concat(prefixCls, "-").concat(type), (_classNames2 = {}, _defineProperty(_classNames2, "".concat(prefixCls, "-with-description"), !!description), _defineProperty(_classNames2, "".concat(prefixCls, "-no-icon"), !isShowIcon), _defineProperty(_classNames2, "".concat(prefixCls, "-banner"), !!banner), _defineProperty(_classNames2, "".concat(prefixCls, "-rtl"), direction === "rtl"), _classNames2), className);
  var dataOrAriaProps = getDataOrAriaProps(props);
  return /*#__PURE__*/_jsx(CSSMotion, {
    visible: !closed,
    motionName: "".concat(prefixCls, "-motion"),
    motionAppear: false,
    motionEnter: false,
    onLeaveStart: function onLeaveStart(node) {
      return {
        maxHeight: node.offsetHeight
      };
    },
    onLeaveEnd: afterClose,
    children: function children(_ref2) {
      var motionClassName = _ref2.className,
        motionStyle = _ref2.style;
      return /*#__PURE__*/_jsxs("div", _objectSpread(_objectSpread({
        ref: ref,
        "data-show": !closed,
        className: classNames(alertCls, motionClassName),
        style: _objectSpread(_objectSpread({}, style), motionStyle),
        onMouseEnter: onMouseEnter,
        onMouseLeave: onMouseLeave,
        onClick: onClick,
        role: "alert"
      }, dataOrAriaProps), {}, {
        children: [isShowIcon ? renderIconNode() : null, /*#__PURE__*/_jsxs("div", {
          className: "".concat(prefixCls, "-content"),
          children: [message ? /*#__PURE__*/_jsx("div", {
            className: "".concat(prefixCls, "-message"),
            children: message
          }) : null, description ? /*#__PURE__*/_jsx("div", {
            className: "".concat(prefixCls, "-description"),
            children: description
          }) : null]
        }), action ? typeof action === "function" && ! /*#__PURE__*/React.isValidElement(action) ? action(close) : /*#__PURE__*/_jsx("div", {
          className: "".concat(prefixCls, "-action"),
          children: action
        }) : null, renderCloseIcon()]
      }));
    }
  });
};
Alert.ErrorBoundary = ErrorBoundary;
export default Alert;